import React from 'react'
import { css } from '@emotion/core'
import { ContentCompose } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { colors, Paragraph, WhiteTheme } from '@fs/zion-ui'
import { useFeatureFlag } from '@fs/zion-flags'
import TextPickerOverlay from './TextPickerOverlay/TextPickerOverlay'
import { useMemoryBook } from '../../../../providers/MemoryBookProvider'
import { getHasDarkBackground } from '../../../../helpers/getHasDarkBackground'

const skeletonTextPickerContainerCSS = (size, dottedBorderFlag) => css`
  display: flex;
  flex-direction: column;
  gap: 5px;
  position: relative;

  width: ${(() => {
    if (dottedBorderFlag) {
      return size === 'quarter' ? '85%' : '93%'
    }
    return '100%'
  })()};
  height: ${dottedBorderFlag ? '90%' : '100%'};

  @media print {
    width: 100%;
  }
`
const skeletonTextPickerBarCSS = (hasDarkBackground) => css`
  background-color: ${hasDarkBackground ? colors.gray80 : colors.gray03};
  width: 100%;
  height: 30px;
  position: relative;
  flex-grow: 1;
`

const skeletonBarsWrapperCSS = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
  button:focus & {
    outline: 3px solid black;
    outline-offset: 2px;
  }
`

const skeletonPencilIconWrapperCSS = css`
  position: absolute;
  top: 50%;
  inset-inline-start: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;

  @media print {
    p {
      display: none;
    }
  }
`

const skeletonButtonCSS = (dottedBorderFlag) => css`
  all: unset;
  ${dottedBorderFlag &&
  `background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23A9A9A9' stroke-width='4' stroke-dasharray='12%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");`}
  ${dottedBorderFlag && `padding: 15px;`}
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 99%;
  cursor: pointer;

  @media print {
    background-image: none;
    padding: 0;
  }
`

export default function TextPickerSkeleton({ overlay, size, positionIndex, pageNumber, handleMemoryUpdate }) {
  const [t] = useTranslation()
  const dottedBorderFlag = useFeatureFlag('chinese_memoryBooks_dottedBorder').isOn
  const { memoryBook } = useMemoryBook()
  const hasDarkBackground = getHasDarkBackground(pageNumber, memoryBook)

  const skeletonTextPickerBars = []
  const numberOfBars = size === 'full' ? 19 : 9

  for (let i = 0; i < numberOfBars; i++) {
    skeletonTextPickerBars.push(<div key={i} aria-hidden="true" css={skeletonTextPickerBarCSS(hasDarkBackground)} />)
  }

  return (
    <>
      <button
        css={[skeletonButtonCSS(dottedBorderFlag), skeletonTextPickerContainerCSS(size, dottedBorderFlag)]}
        onClick={overlay.handleClick}
        aria-label={t('TextPicker.openEditor.label', 'Open Story Editor')}
        type="button"
        data-testid="text-picker-skeleton"
      >
        <div css={skeletonPencilIconWrapperCSS}>
          {hasDarkBackground ? (
            <WhiteTheme>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <ContentCompose size="md" />
                <Paragraph size="xs">{t('TextPicker.addText.label', 'Add Text')}</Paragraph>
              </div>
            </WhiteTheme>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <ContentCompose size="md" />
              <Paragraph size="xs">{t('TextPicker.addText.label', 'Add Text')}</Paragraph>
            </div>
          )}
        </div>

        <div css={skeletonBarsWrapperCSS}> {skeletonTextPickerBars}</div>
      </button>
      {(overlay.isOpen || overlay.transitioning) && (
        <TextPickerOverlay
          overlay={overlay}
          positionIndex={positionIndex}
          size={size}
          pageNumber={pageNumber}
          handleMemoryUpdate={handleMemoryUpdate}
        />
      )}
    </>
  )
}
